import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import axios from "axios";
import { paymentStatus } from "@/lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const pkgSlug = params.get("pkg");
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const [pkg, setPkg] = useState(null);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let tries = 0;
    let active = true;
    const poll = async () => {
      if (!active) return;
      try {
        // Package checkout path first (has package metadata)
        if (pkgSlug) {
          const r = await axios.get(`${API}/packages/checkout/status/${sessionId}`);
          const d = r.data;
          setPkg(d);
          if (d.payment_status === "paid") { setState("paid"); return; }
        } else {
          const d = await paymentStatus(sessionId);
          if (d.payment_status === "paid") { setState("paid"); return; }
          if (d.payment_status === "failed" || d.payment_status === "expired") { setState("error"); return; }
        }
      } catch { /* keep trying */ }
      tries += 1;
      if (tries >= MAX_POLLS) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [sessionId, pkgSlug]);

  return (
    <>
      <Navbar />
      <main className="min-h-screen flex items-center justify-center bg-slate-50 px-4" data-testid="payment-success">
        <div className="bg-white border border-slate-200 rounded-3xl card-shadow p-10 max-w-md w-full text-center">
          {state === "checking" && (
            <>
              <Loader2 className="animate-spin text-brand mx-auto" size={40} />
              <h1 className="font-heading text-2xl font-bold text-slate-900 mt-5">Confirming your payment…</h1>
              <p className="text-slate-500 mt-2 text-sm">This only takes a moment.</p>
            </>
          )}
          {state === "paid" && (
            <>
              <CheckCircle2 className="text-emerald-500 mx-auto" size={48} />
              <h1 className="font-heading text-2xl font-bold text-slate-900 mt-5">You're all set! 🎉</h1>
              {pkg?.package_tier ? (
                <>
                  <p className="text-slate-500 mt-2">Welcome to <b className="uppercase text-slate-900">{pkg.package_tier}</b>. Your growth dashboard is ready.</p>
                  <Link to="/my" data-testid="success-my-link" className="mt-7 inline-flex items-center gap-2 bg-emerald-600 text-white font-semibold rounded-xl px-6 py-3.5 hover:-translate-y-0.5 transition-transform">
                    Go to my dashboard <ArrowRight size={17} />
                  </Link>
                </>
              ) : (
                <>
                  <p className="text-slate-500 mt-2">Your plan is active. Head to your portal to build and embed your chatbots.</p>
                  <Link to="/portal" data-testid="success-portal-link" className="mt-7 inline-flex items-center gap-2 bg-brand text-white font-semibold rounded-xl px-6 py-3.5 hover:-translate-y-0.5 transition-transform">
                    Go to my portal <ArrowRight size={17} />
                  </Link>
                </>
              )}
            </>
          )}
          {(state === "timeout" || state === "error") && (
            <>
              <XCircle className="text-amber-500 mx-auto" size={44} />
              <h1 className="font-heading text-2xl font-bold text-slate-900 mt-5">{state === "timeout" ? "Still processing" : "Something went wrong"}</h1>
              <p className="text-slate-500 mt-2 text-sm">{state === "timeout" ? "Your payment is taking longer than usual to confirm. It may still complete — check your portal shortly." : "We couldn't confirm this payment. If you were charged, contact support and we'll sort it out."}</p>
              <Link to="/portal" className="mt-7 inline-flex items-center gap-2 bg-slate-900 text-white font-semibold rounded-xl px-6 py-3.5">Go to my portal</Link>
            </>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
